import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Coins, FileText, Warning, Clock, TrendUp, Wallet, Buildings } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const fmtNum = (v) => (v || 0).toLocaleString("pt-BR");
const COLORS = ["#F59E0B", "#FCD34D", "#B8941F", "#8B6F14", "#5C4A0F"];

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className="tce-card p-6 fade-up">
      <div className="flex items-start justify-between">
        <div>
          <div className="uppercase-label">{label}</div>
          <div className={`font-heading text-3xl mt-3 tracking-tight ${accent ? "gold-text" : ""}`}>{value}</div>
        </div>
        <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center text-[#F59E0B]">
          <Icon size={20} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

function MunicipiosBarChart({ data }) {
  return (
    <div className="tce-card p-6 lg:col-span-2">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="uppercase-label">Top municípios</div>
          <div className="font-heading text-xl mt-1">Valor por município</div>
        </div>
        <TrendUp size={20} className="text-[#F59E0B]" />
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data.slice(0, 8)} margin={{ left: 10, right: 10 }}>
          <defs>
            <linearGradient id="goldBar" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FCD34D" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#334155" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="nome" stroke="#94A3B8" fontSize={11} angle={-20} textAnchor="end" height={70} />
          <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `R$${(v / 1000000).toFixed(1)}M`} />
          <Tooltip
            contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 8, color: "#E9EEF5" }}
            formatter={(v) => fmtBRL(v)}
          />
          <Bar dataKey="valor" fill="url(#goldBar)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ModalidadePieChart({ data }) {
  return (
    <div className="tce-card p-6">
      <div className="uppercase-label">Distribuição</div>
      <div className="font-heading text-xl mt-1 mb-6">Por modalidade</div>
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie data={data} dataKey="count" nameKey="modalidade"
               cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
            {data.map((entry, i) => (
              <Cell key={entry.modalidade} fill={COLORS[i % COLORS.length]} stroke="#1E293B" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 8 }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="space-y-1.5 mt-3">
        {data.slice(0, 5).map((m, i) => (
          <div key={m.modalidade} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
              {m.modalidade}
            </div>
            <span className="text-[#E9EEF5] font-medium">{m.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopFornecedoresPanel({ items }) {
  if (!items || items.length === 0) return null;
  const maxVal = Math.max(...items.map((i) => i.total));
  return (
    <div className="tce-card p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B]">
            <Buildings size={20} weight="duotone" />
          </div>
          <div>
            <div className="uppercase-label">Análise anti-direcionamento</div>
            <div className="font-heading text-xl mt-1">Top Fornecedores por Valor</div>
          </div>
        </div>
        <span className="text-xs text-[#94A3B8]">Top {items.length}</span>
      </div>
      <div className="space-y-3">
        {items.map((f, i) => (
          <div key={f.cnpj} data-testid={`top-forn-${i}`} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-xs px-2 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] font-mono">#{i + 1}</span>
                <span className="text-[#E9EEF5] truncate" title={f.nome}>{f.nome || "—"}</span>
                <span className="text-xs text-[#94A3B8] font-mono whitespace-nowrap">CNPJ {f.cnpj}</span>
              </div>
              <span className="text-[#FCD34D] font-mono ml-3 whitespace-nowrap">{fmtBRL(f.total)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-[#1E293B] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#B8941F] to-[#FCD34D]"
                     style={{ width: `${(f.total / maxVal) * 100}%` }} />
              </div>
              <span className="text-[10px] text-[#94A3B8] whitespace-nowrap">
                {f.contratos} contrato{f.contratos !== 1 ? "s" : ""}
                {f.municipios_count > 1 && ` · ${f.municipios_count} municípios`}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SaldoCriticoTable({ items }) {
  if (items.length === 0) return null;
  return (
    <div className="tce-card p-6 border-[#EF4444]/30">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#EF4444]/15 flex items-center justify-center text-[#EF4444]">
            <Wallet size={20} weight="duotone" />
          </div>
          <div>
            <div className="uppercase-label !text-[#EF4444]">Alerta financeiro</div>
            <div className="font-heading text-xl mt-1">Contratos com saldo crítico (≥ 80% consumido)</div>
          </div>
        </div>
        <span className="text-sm text-[#EF4444] font-mono font-bold">{items.length}</span>
      </div>
      <div className="overflow-hidden rounded-md border border-[#EF4444]/20">
        <table className="w-full text-sm">
          <thead className="bg-[#EF4444]/5">
            <tr className="text-left">
              <th className="px-4 py-3 uppercase-label">Contrato</th>
              <th className="px-4 py-3 uppercase-label">Município</th>
              <th className="px-4 py-3 uppercase-label text-right">Total</th>
              <th className="px-4 py-3 uppercase-label text-right">Saldo</th>
              <th className="px-4 py-3 uppercase-label text-right">Consumido</th>
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 10).map((c) => (
              <tr key={c.id} className="border-t border-[#334155] hover:bg-[#1E293B]/60">
                <td className="px-4 py-3">
                  <Link to={`/contratos/${c.id}`} className="font-mono text-[#F59E0B] hover:text-[#FCD34D]">{c.numero}</Link>
                  <div className="text-xs text-[#94A3B8] mt-0.5 max-w-[280px] truncate">{c.objeto}</div>
                </td>
                <td className="px-4 py-3 text-[#94A3B8]">{c.municipio_nome}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{fmtBRL(c.valor)}</td>
                <td className={`px-4 py-3 text-right font-mono ${c.saldo < 0 ? "text-[#EF4444]" : "text-[#FCD34D]"}`}>{fmtBRL(c.saldo)}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`text-xs font-bold ${c.pct_consumido >= 100 ? "text-[#EF4444]" : "text-[#F59E0B]"}`}>
                    {c.pct_consumido.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VencimentosTable({ items }) {
  return (
    <div className="tce-card p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="uppercase-label">Atenção</div>
          <div className="font-heading text-xl mt-1">Contratos com vencimento próximo</div>
        </div>
        <span className="text-xs text-[#F59E0B] flex items-center gap-1.5">
          <Warning size={14} weight="fill" /> Próximos 60 dias
        </span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-[#94A3B8] py-6 text-center">Nenhum contrato com vencimento próximo.</div>
      ) : (
        <div className="overflow-hidden rounded-md border border-[#334155]">
          <table className="w-full text-sm">
            <thead className="bg-[#1E293B]">
              <tr className="text-left">
                <th className="px-4 py-3 uppercase-label">Número</th>
                <th className="px-4 py-3 uppercase-label">Objeto</th>
                <th className="px-4 py-3 uppercase-label">Município</th>
                <th className="px-4 py-3 uppercase-label">Vencimento</th>
                <th className="px-4 py-3 uppercase-label text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 8).map((a) => (
                <tr key={a.id} className="border-t border-[#334155] hover:bg-[#1E293B]">
                  <td className="px-4 py-3 font-mono text-[#F59E0B]">{a.numero}</td>
                  <td className="px-4 py-3 text-[#E9EEF5] max-w-[300px] truncate">{a.objeto}</td>
                  <td className="px-4 py-3 text-[#94A3B8]">{a.municipio_nome}</td>
                  <td className="px-4 py-3 text-[#F59E0B]">{a.data_fim?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">{fmtBRL(a.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 text-right">
        <Link to="/contratos" className="text-sm text-[#F59E0B] hover:text-[#FCD34D]" data-testid="link-all-contracts">
          Ver todos os contratos →
        </Link>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [saldoAlertas, setSaldoAlertas] = useState([]);
  const [topForn, setTopForn] = useState([]);
  const [anos, setAnos] = useState([]);
  const [filtroMun, setFiltroMun] = useState("");
  const [filtroAno, setFiltroAno] = useState("");
  const [munis, setMunis] = useState([]);

  const loadAll = () => {
    api.get("/municipalities").then((r) => setMunis(r.data));
    api.get("/dashboard/anos-disponiveis").then((r) => setAnos(r.data.anos || []));
    api.get("/dashboard/alertas").then((r) => setAlertas(r.data));
    api.get("/dashboard/saldo-alertas?threshold=80").then((r) => setSaldoAlertas(r.data.items));
  };

  useEffect(loadAll, []);
  useAutoRefresh(loadAll, 30000);

  useEffect(() => {
    const params = {};
    if (filtroMun) params.municipio_id = filtroMun;
    if (filtroAno) params.ano = filtroAno;
    api.get("/dashboard/stats", { params }).then((r) => setStats(r.data));
    api.get("/dashboard/top-fornecedores", { params: { ...params, limit: 10 } })
      .then((r) => setTopForn(r.data.items || []));
  }, [filtroMun, filtroAno]);

  if (!stats) return <div className="text-[#94A3B8]">Carregando painel…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="uppercase-label">Visão geral</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Painel de Controle</h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="filter-year"
            value={filtroAno}
            onChange={(e) => setFiltroAno(e.target.value)}
            className="tce-input px-4 py-2.5 rounded-md text-sm"
          >
            <option value="">Todos os anos</option>
            {anos.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select
            data-testid="filter-municipality"
            value={filtroMun}
            onChange={(e) => setFiltroMun(e.target.value)}
            disabled={!isAdmin}
            className="tce-input px-4 py-2.5 rounded-md text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">{isAdmin ? "Todos os municípios" : (user?.municipio_nome || "Seu município")}</option>
            {munis.map((m) => (
              <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat testid="stat-total" label="Total de contratos" value={fmtNum(stats.total_contratos)} icon={FileText} />
        <Stat testid="stat-valor" label="Valor consolidado" value={fmtBRL(stats.valor_total)} icon={Coins} accent />
        <Stat testid="stat-vencendo" label="Vencendo em 30 dias" value={fmtNum(stats.vencendo_30d)} icon={Clock} />
        <Stat testid="stat-vencidos" label="Contratos vencidos" value={fmtNum(stats.vencidos)} icon={Warning} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <MunicipiosBarChart data={stats.por_municipio} />
        <ModalidadePieChart data={stats.por_modalidade} />
      </div>

      <TopFornecedoresPanel items={topForn} />
      <SaldoCriticoTable items={saldoAlertas} />
      <VencimentosTable items={alertas} />
    </div>
  );
}
